import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Stack } from '../stack/Stack';
import { getHighlightColor } from '../../utils/getHighlightColor';
import {
  Dog,
  Cat,
  Rabbit,
  Horse,
  Calendar,
  Money,
  Mobiledevice,
  Flowers,
  Brokenheart,
  Stethoscope,
  Bulletlist,
  Shelter,
  Facebook,
  Star,
  Numberone,
  Numbertwo,
  Numberthree,
  Paw,
  Shieldwithtick,
  ArrowRight,
  World,
} from '../icon';
import { Typography } from '../typography/Typography';
import { Link } from '../link/Link';
import { useBackgroundColor } from '../../utils';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
// Interface

const IconMap = {
  Dog,
  Cat,
  Rabbit,
  Horse,
  Calendar,
  Money,
  Mobiledevice,
  Flowers,
  Brokenheart,
  Stethoscope,
  Bulletlist,
  Shelter,
  Facebook,
  Star,
  Numberone,
  Numbertwo,
  Numberthree,
  Paw,
  Shieldwithtick,
  World,
};

export interface IconCardProps {
  addBorder?: boolean;
  icon?: keyof typeof IconMap;
  colorCode: string;
  heading?: string;
  description?: string;
  link?: {
    label: string;
    url: string;
    target?: string;
  };
}

interface StyledIconCardProps {
  addBorder?: boolean;
}

interface StyledIconContainerProps {
  backgroundColor?: ThemeColorsKeys;
}

// Styles

const StyledIconCard = styled.div<StyledIconCardProps>`
  ${({ theme: { space, colors, borderRadii }, addBorder }) => css`
    border-radius: ${borderRadii.medium};
    background-color: ${colors.utilityWhite};
    padding: ${space.fluidMedium};
    display: flex;
    justify-content: center;
    align-items: flex-start;
    color: ${colors.utilityText};
    text-align: center;
    border: 1px solid ${colors.utilityWhite};
    list-style: none;

    ${addBorder &&
    css`
      border: 1px solid ${colors.brandOne100};
    `};

    > * {
      width: 100%;
    }

    a {
      color: ${colors.utilityText};
    }
  `};
`;

const StyledOuterStack = styled(Stack)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const StyledStack = styled(Stack)`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const StyledIconContainer = styled.div<StyledIconContainerProps>`
  ${({ theme: { space }, backgroundColor }) => css`
    border-radius: 50%;
    padding: ${space.small};
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
    width: ${space.fluidXxxLarge};
    height: ${space.fluidXxxLarge};

    ${backgroundColor &&
    css`
      ${useBackgroundColor(backgroundColor)};
    `};
  `};
`;

const StyledIcon = styled.div`
  ${({ theme: { space, colors } }) => css`
    width: ${space.fluidXLarge};
    height: ${space.fluidXLarge};
    color: ${colors.utilityWhite};

    svg {
      width: 100%;
      height: 100%;
    }
  `};
`;

const StyledLine = styled.div`
  ${({ theme: { colors } }) => css`
    width: 100%;
    height: 1px;
    background-color: ${colors.brandOne20};
  `};
`;

// JSX

export const IconCard: FC<IconCardProps> = ({
  addBorder,
  icon,
  colorCode,
  heading,
  description,
  link,
}) => {
  let Icon = null;

  // Check that all icons are defined
  Object.entries(IconMap).forEach(([key, value]) => {
    if (!value) {
      console.error(`Icon with key ${key} is undefined.`);
    }
  });

  if (icon) {
    const normalizedIcon = icon.replace(/\s+/g, '');
    const dynamicIconKey =
      normalizedIcon.charAt(0).toUpperCase() + normalizedIcon.slice(1);
    Icon = IconMap[dynamicIconKey as keyof typeof IconMap];
  }

  return (
    <StyledIconCard addBorder={addBorder} role="listitem">
      <StyledOuterStack gap="large">
        <StyledStack gap="fluidMedium">
          {Icon && (
            <StyledIconContainer
              backgroundColor={
                colorCode ? getHighlightColor(colorCode) : 'brandOne100'
              }
              role="presentation"
            >
              <StyledIcon>
                <Icon />
              </StyledIcon>
            </StyledIconContainer>
          )}
          <Stack gap="xxSmall">
            <Typography variant="headingSmall" component="h3">
              {heading}
            </Typography>
            {description && (
              <Typography variant="bodyMedium">{description}</Typography>
            )}
          </Stack>
        </StyledStack>
        {link && (
          <StyledStack gap="fluidSmall">
            <StyledLine />
            <Link
              label={link?.label}
              url={link?.url}
              icon={<ArrowRight />}
              iconOnRight
            />
          </StyledStack>
        )}
      </StyledOuterStack>
    </StyledIconCard>
  );
};

export default IconCard;
